import { useInfiniteQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { sortBy } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { CommentDto } from "@/api/types";
import { CommentField } from "@/components/CommentField/CommentField";
import {
  createMentionComposerValue,
  getStructuredMentionFields,
  type MentionComposerValue,
} from "@/components/CommunityFeed/MentionComposer";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ArrowDownIcon } from "@/components/Icons/ArrowDownIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { useImageInput } from "@/components/ImageInput/useImageInput";
import { InactivePostWarning } from "@/components/InactivePostWarning/InactivePostWarning";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { isVideoUploaded, useVideoInput } from "@/components/VideoInput/useVideoInput";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useBool } from "@/hooks/useBool";
import { useSignalRHub, useSignalRSubscription } from "@/hooks/useSignalR";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { useTranslation } from "@/translations";

import { CodeOfConductCommentDeletionModal } from "./CodeOfConductCommentDeletionModal";
import type { CreateCommentPayload, EditCommentPayload } from "./Comment";
import { Comment } from "./Comment";
import type { CommunityMessageActivity } from "./types";

interface Props {
  /** Message-compatible activity data required for comment permissions, status, and routing. */
  message: CommunityMessageActivity;
  commentPost: ({ parentId, comment, images, videos, failureMessage }: CreateCommentPayload) => Promise<void>;
  editComment: ({ commentId, comment, images, videos, failureMessage }: EditCommentPayload) => Promise<void>;
  deleteComment: (commentId: string) => void;
  onViewCommentReactions: (commentId: string, amountReactions: number) => void;
}
/** Renders a message's comments and coordinates create, edit, delete, reaction, and pagination mutations. */
export function CommentList({
  message,
  commentPost,
  editComment,
  deleteComment,
  onViewCommentReactions,
}: Props): React.ReactNode {
  const [images, setImages] = useState<FormImage[]>([]);
  const [videos, setVideos] = useState<FormVideo[]>([]);
  const [showNewCommentPill, newCommentPillHandler] = useBool(false);
  const [editingComment, setEditingComment] = useState<string | undefined>(undefined);
  const [mentionValue, setMentionValue] = useState<MentionComposerValue>({ content: "" });
  const [deletingComment, setDeletingComment] = useState<CommentDto | undefined>(undefined);
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: commentsData,
    fetchNextPage: fetchMoreComments,
    refetch: refetchComments,
    isFetching: isFetchingComments,
    hasNextPage: hasMoreComments,
  } = useInfiniteQuery({
    ...communityFeedQueries.commentsInfinite(message.id),
  });
  const { getValues, setValue, control, register } = useForm<{
    commentValue: string;
  }>();
  const commentValue = useWatch({
    control,
    name: "commentValue",
  });
  const { signalRConnection } = useSignalRHub("community-feed-detail-hub", {
    query: `userId=${sessionUser.id}&entityId=${message.id}&entityType=Message`,
  });
  const onNewComment = useCallback(
    (
      ...args: [
        {
          entityId: string;
          entityType: "message" | "comment" | "poll" | "survey";
          authorId: string;
        },
      ]
    ) => {
      if (args[0].authorId === sessionUser.id) {
        return;
      }
      newCommentPillHandler.setTrue();
    },
    [sessionUser, newCommentPillHandler],
  );
  useSignalRSubscription(signalRConnection, SIGNALR_EVENTS.COMMUNITY_FEED.NEW_COMMENT, onNewComment);
  useEffect(() => {
    register("commentValue");
  }, [register]);
  const { addImages, removeImage, removeImages } = useImageInput({
    selectedImages: images,
    onChange: setImages,
  });
  const { uploadFormVideo } = useUploadVideo({
    onProgress: ({ name, progress }) => {
      setVideos((prevState) =>
        prevState.map((video) => {
          if (!isVideoUploaded(video) && video.file.name === name) {
            return {
              ...video,
              uploadProgress: progress,
            };
          } else {
            return video;
          }
        }),
      );
    },
  });
  const { addVideos, removeVideo, removeVideos } = useVideoInput({
    selectedVideos: videos,
    onChange: setVideos,
    uploadFn: uploadFormVideo,
  });
  async function handleAddComment() {
    if (editingComment) {
      await editComment({
        commentId: editingComment,
        comment: getValues("commentValue"),
        ...getStructuredMentionFields(mentionValue),
        images,
        videos,
        failureMessage: t("component.community-post.comments.edit-error"),
      });
      setEditingComment(undefined);
    } else {
      await commentPost({
        comment: getValues("commentValue"),
        ...getStructuredMentionFields(mentionValue),
        images,
        videos,
        failureMessage: t("component.community-post.comments.error"),
      });
    }
    removeImages();
    removeVideos();
    setValue("commentValue", "");
    setMentionValue({ content: "" });
  }
  const handleEditComment = useCallback(
    (comment: CommentDto) => {
      setValue("commentValue", comment.content || "");
      setMentionValue(createMentionComposerValue(comment));
      if (comment.image) {
        setImages([comment.image]);
      }
      if (comment.videos.length > 0) {
        setVideos(comment.videos);
      }
      setEditingComment(comment.id);
    },
    [setValue, setImages, setEditingComment],
  );
  const handleDeleteComment = useCallback((comment: CommentDto) => {
    setDeletingComment(comment);
  }, []);
  const handleCancelEdit = useCallback(() => {
    setValue("commentValue", "");
    setMentionValue({ content: "" });
    removeImages();
    removeVideos();
    setEditingComment(undefined);
  }, [setValue, removeImages, removeVideos]);
  const onChangeContent = useCallback((value: string) => setValue("commentValue", value), [setValue]);
  const onChangeMention = useCallback(
    (value: MentionComposerValue) => {
      setMentionValue(value);
      setValue("commentValue", value.content);
    },
    [setValue],
  );
  async function handleLoadMoreComments() {
    newCommentPillHandler.setFalse();
    await refetchComments();
  }
  const allComments = useMemo(() => {
    return commentsData?.pages.flatMap((x) => x.items ?? []) ?? [];
  }, [commentsData?.pages]);
  const commentsByDate = useMemo(() => sortBy(allComments, (x) => parseISO(x.postedAt).valueOf()), [allComments]);

  return (
    <>
      <section className="relative flex flex-col gap-space-md">
        {hasMoreComments && (
          <Button variant="ghostPrimary" data-testid="comments-load-more" onClick={() => fetchMoreComments()}>
            <span className="text-caption">{t("component.community-post.comments.see-more")}</span>
          </Button>
        )}
        {isFetchingComments && (
          <Box padding={{ y: "md" }}>
            <Inline justify="center">
              <Spinner size="md" />
            </Inline>
          </Box>
        )}
        {commentsByDate.length > 0 && (
          <Stack gap="lg">
            {commentsByDate.map((comment) => (
              <Comment
                key={comment.id}
                comment={comment}
                onEdit={handleEditComment}
                onDelete={handleDeleteComment}
                message={message}
                onAddComment={commentPost}
                onEditComment={editComment}
                onViewCommentReactions={onViewCommentReactions}
              />
            ))}
          </Stack>
        )}

        {(message.canComment || editingComment) && !isFetchingComments && (
          <form className="flex items-end">
            <CommentField
              user={sessionUser}
              value={commentValue}
              onChange={onChangeContent}
              mentionValue={mentionValue}
              onMentionChange={onChangeMention}
              messageId={message.id}
              images={images}
              videos={videos}
              onSubmit={handleAddComment}
              isEdit={editingComment !== undefined}
              onCancel={editingComment ? handleCancelEdit : undefined}
              allowedAttachments={["image", "video"]}
              onRemoveVideo={removeVideo}
              onAddVideo={addVideos}
              onRemoveImage={removeImage}
              onAddImages={addImages}
              tooltips={{
                root:
                  !editingComment && message.isInactivePost
                    ? ({ onClose }) => <InactivePostWarning {...{ onClose, message }} />
                    : undefined,
              }}
              placeholder={t("component.comment-field.create-new-reaction")}
            />
          </form>
        )}
        {showNewCommentPill && (
          <div className="absolute bottom-12 left-1/2 w-fit -translate-x-1/2">
            <Button icon={<ArrowDownIcon />} onClick={handleLoadMoreComments} isCircular>
              {t("component.community-post.comments.load-new")}
            </Button>
          </div>
        )}
      </section>
      {deletingComment?.canDeleteOwn ? (
        <ConfirmModal
          isOpened={!!deletingComment}
          title={t("component.community-post.comments.delete.modal.title")}
          description={t("component.community-post.comments.delete.modal.description")}
          isLoading={false}
          tone="error"
          onReject={() => setDeletingComment(undefined)}
          onOpenChange={(state) => {
            if (!state) {
              setDeletingComment(undefined);
            }
          }}
          rejectBtnProps={{
            "data-testid": "delete-comment-modal-cancel",
          }}
          onResolve={() => {
            deleteComment(deletingComment.id);
            setDeletingComment(undefined);
          }}
          resolveBtnProps={{
            text: t("common.action.delete"),
            "data-testid": "modal-confirm-delete",
          }}
          shouldCloseOnEsc
          data-testid="cancel-edit-modal"
        />
      ) : (
        <CodeOfConductCommentDeletionModal
          isOpened={!!deletingComment}
          onOpenChange={(state) => {
            if (!state) {
              setDeletingComment(undefined);
            }
          }}
          onDelete={() => {
            deleteComment(deletingComment!.id);
            setDeletingComment(undefined);
          }}
        />
      )}
    </>
  );
}
